import {lazy, Suspense, useMemo} from 'react';
import {Navigate, Route, Routes, useNavigate} from 'react-router';
import {RequireAuth} from './RequireAuth';
import {AppLayout} from '@/components/layout/AppLayout';
import {LoginPage} from '@/pages/LoginPage';
import {useAtomValue} from 'jotai';
import {cachedSessionAtom} from '@/state/authAtoms.ts';
import {RoleType} from '@/types/types.ts';
import {AdminAuth} from '@/routes/AdminAuth.tsx';
import {routerLinks} from '@/Constants.ts';
import {RouteErrorBoundary} from '@/components/RouteErrorBoundary.tsx';
import {Box, CircularProgress} from '@mui/material';

// Route-level code splitting: every page below (other than LoginPage,
// kept eager since it's the very first thing an unauthenticated session
// sees and shouldn't wait on an extra chunk fetch) is loaded on demand via
// React.lazy instead of a static top-of-file import. Before this, every
// page in the app — including all of the Admin/SuperAdmin-only Setup and
// Reports pages a field User's AdminAuth gate never even renders — was
// bundled into one ~2.3MB main chunk (vite build's own
// "chunk larger than 500 kB" warning). Splitting per-route means a field
// User's initial load only pulls in the pages they can actually reach
// (Wells/Barrel Tests/Run Tickets/Daily Report), and an Admin's Setup/
// Reports pages load on first navigation to them, not on app boot.
const ForgotPage = lazy(() => import('@/pages/ForgotPage.tsx').then((m) => ({default: m.ForgotPage})));
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then((m) => ({default: m.DashboardPage})));
const WellsListPage = lazy(() => import('@/pages/WellsListPage').then((m) => ({default: m.WellsListPage})));
const BarrelTestsPage = lazy(() => import('@/pages/BarrelTestsPage').then((m) => ({default: m.BarrelTestsPage})));
const ReportsPage = lazy(() => import('@/pages/ReportsPage').then((m) => ({default: m.ReportsPage})));
const AuditReportPage = lazy(() => import('@/pages/AuditReportPage').then((m) => ({default: m.AuditReportPage})));
const TanksAllocationsReportPage = lazy(() =>
   import('@/pages/TanksAllocationsReportPage.tsx').then((m) => ({default: m.TanksAllocationsReportPage})),
);
const ProductionReportPage = lazy(() =>
   import('@/pages/ProductionReportPage.tsx').then((m) => ({default: m.ProductionReportPage})),
);
const Ogp03iExportPage = lazy(() =>
   import('@/pages/Ogp03iExportPage.tsx').then((m) => ({default: m.Ogp03iExportPage})),
);
const UsersAdminPage = lazy(() =>
   import('@/pages/admin/UsersAdminPage.tsx').then((m) => ({default: m.UsersAdminPage})),
);
const WellsAdminPage = lazy(() =>
   import('@/pages/admin/WellsAdminPage.tsx').then((m) => ({default: m.WellsAdminPage})),
);
const WellEditPage = lazy(() => import('@/pages/admin/WellEditPage.tsx').then((m) => ({default: m.WellEditPage})));
const LeaseAdminPage = lazy(() =>
   import('@/pages/admin/LeaseAdminPage.tsx').then((m) => ({default: m.LeaseAdminPage})),
);
// Price Config removed per Duane, 2026-08-28 — see TopNav.tsx's SETUP_MENU_ITEMS comment.
// import {PriceConfigPage} from '@/pages/admin/PriceConfigPage.tsx';
const DailyReportPage = lazy(() =>
   import('@/pages/wells/DailyReportPage.tsx').then((m) => ({default: m.DailyReportPage})),
);
const BarrelTestTrendPage = lazy(() =>
   import('@/pages/BarrelTestTrendPage').then((m) => ({default: m.BarrelTestTrendPage})),
);
const RunTicketsPage = lazy(() => import('@/pages/RunTicketsPage').then((m) => ({default: m.RunTicketsPage})));
const PulledBottomsPage = lazy(() => import('@/pages/PulledBottomsPage').then((m) => ({default: m.PulledBottomsPage})));

// Same full-page centered spinner RequireAuth.tsx already uses for its
// own 'loading' auth-bootstrap state, reused here so a lazy page's chunk
// fetch looks identical to that existing loading state rather than
// introducing a second visual language for "the app is loading something."
function RouteFallback() {
   return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
         <CircularProgress />
      </Box>
   );
}

export function AppRoutes() {
   const cache = useAtomValue(cachedSessionAtom);
   const navigate = useNavigate();

   const defaultRoute = useMemo(() => {
      switch (cache?.role) {
         case RoleType.Admin:
            return '/dashboard';
         case RoleType.User:
            return routerLinks.wells;
         case RoleType.SuperAdmin:
            return '/dashboard';
         default:
            return routerLinks.wells;
      }
   }, [cache?.role]);

   return (
      // See RouteErrorBoundary.tsx's own doc comment — this is what stops a
      // failed lazy-route chunk fetch (most commonly a field User navigating
      // offline to a page this browser tab never downloaded, e.g. Daily
      // Report) from crashing to a blank white screen with nothing else on
      // the page. onReset sends them back to Wells (routerLinks.wells) — a
      // route whose own lazy() chunk already resolved successfully earlier
      // in this page load, so getting there needs no re-import (and thus no
      // network) even if the one that just failed still would.
      <RouteErrorBoundary onReset={() => navigate(routerLinks.wells, {replace: true})}>
         <Suspense fallback={<RouteFallback />}>
            <Routes>
               <Route path={routerLinks.login} element={<LoginPage />} />
               <Route path={routerLinks.forgotPassword} element={<ForgotPage />} />

               <Route element={<RequireAuth />}>
                  {/* Mobile Daily Input page — deliberately outside AppLayout so it
                renders full-screen with no TopNav/BottomNav, matching the
                mockup. Desktop reaches the same data through DailyReportPanel
                (a drawer) rendered from within WellsListPage instead. */}
                  <Route path={routerLinks.dailyReport} element={<DailyReportPage />} />
                  <Route path={routerLinks.barrelTestTrend} element={<BarrelTestTrendPage />} />
                  <Route element={<AppLayout />}>
                     <Route path='/' element={<Navigate to={defaultRoute} replace />} />
                     <Route element={<AdminAuth />}>
                        <Route path={routerLinks.dashboard} element={<DashboardPage />} />
                        <Route path={routerLinks.reports} element={<ReportsPage />} />
                        <Route path={routerLinks.auditReport} element={<AuditReportPage />} />
                        <Route path={routerLinks.tanksAllocationsReport} element={<TanksAllocationsReportPage />} />
                        <Route path={routerLinks.productionReport} element={<ProductionReportPage />} />
                        <Route path={routerLinks.ogp03iExport} element={<Ogp03iExportPage />} />
                        <Route path={routerLinks.userListSetup} element={<UsersAdminPage />} />
                        <Route path={routerLinks.wellListSetup} element={<WellsAdminPage />} />
                        <Route path={routerLinks.wellSetup} element={<WellEditPage />} />
                        {/* LeaseAdminPage handles both — no lease selected (list only) and a lease
                      selected (list + LeaseEditPage inline) — off the same :leaseId param, so
                      both routes point at it rather than nesting a child Route/Outlet. See
                      the comment atop LeaseAdminPage.tsx. */}
                        <Route path={routerLinks.leaseListSetup} element={<LeaseAdminPage />} />
                        <Route path={routerLinks.leaseSetup} element={<LeaseAdminPage />} />
                        {/* <Route path={routerLinks.priceConfigSetup} element={<PriceConfigPage />} /> */}
                     </Route>
                     <Route path={routerLinks.wells} element={<WellsListPage />} />
                     <Route path={routerLinks.barrelTests} element={<BarrelTestsPage />} />
                     <Route path={routerLinks.runTickets} element={<RunTicketsPage />} />
                     <Route path={routerLinks.pulledBottoms} element={<PulledBottomsPage />} />
                  </Route>
               </Route>

               <Route path='*' element={<Navigate to={'/'} replace />} />
            </Routes>
         </Suspense>
      </RouteErrorBoundary>
   );
}
